/**
 * ClassificationBadge - Color-coded classification pill for inbox interactions.
 *
 * Visual spec from task 2569 §6:
 * - support   → var(--info)           badge
 * - sales     → var(--accent-primary) badge
 * - spam      → var(--text-muted)     badge
 * - complaint → var(--warning)        badge
 * - unclassified → neutral
 */

import type { CSSProperties, ReactNode } from 'react'
import type { InteractionClassification } from '../types'

// ── Types ────────────────────────────────────────────────────────────────────

interface ClassificationBadgeProps {
  classification: InteractionClassification
  size?: 'sm' | 'md'
}

// ── Color Map ─────────────────────────────────────────────────────────────────

const classificationColorMap: Record<
  InteractionClassification,
  { color: string; bg: string }
> = {
  support: {
    color: 'var(--info)',
    bg: 'color-mix(in srgb, var(--info) 12%, transparent)',
  },
  sales: {
    color: 'var(--accent-primary)',
    bg: 'color-mix(in srgb, var(--accent-primary) 12%, transparent)',
  },
  spam: {
    color: 'var(--text-muted)',
    bg: 'var(--bg-tertiary)',
  },
  complaint: {
    color: 'var(--warning)',
    bg: 'color-mix(in srgb, var(--warning) 12%, transparent)',
  },
  unclassified: {
    color: 'var(--text-secondary)',
    bg: 'var(--bg-tertiary)',
  },
}

const classificationLabel: Record<InteractionClassification, string> = {
  support: 'Support',
  sales: 'Sales',
  spam: 'Spam',
  complaint: 'Complaint',
  unclassified: 'Unclassified',
}

// ── Component ────────────────────────────────────────────────────────────────

export function ClassificationBadge({
  classification,
  size = 'sm',
}: ClassificationBadgeProps): ReactNode {
  const colors = classificationColorMap[classification] ?? classificationColorMap.unclassified

  const style: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    padding: size === 'sm' ? '1px 6px' : '2px 8px',
    fontSize: size === 'sm' ? '11px' : '12px',
    fontWeight: 500,
    borderRadius: '100px',
    color: colors.color,
    background: colors.bg,
    border: `1px solid ${colors.color}`,
    borderColor: `color-mix(in srgb, ${colors.color} 30%, transparent)`,
    whiteSpace: 'nowrap',
    lineHeight: 1.5,
  }

  return (
    <span style={style} aria-label={`Classification: ${classificationLabel[classification]}`}>
      {classificationLabel[classification]}
    </span>
  )
}

export default ClassificationBadge
